import { useEffect, useRef, useState } from 'react';

export type InputCapabilities = {
  coarsePointer: boolean;
  finePointer: boolean;
  canHover: boolean;
  hasHardwareKeyboard: boolean;
};

const MODIFIER_KEYS = new Set([
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'CapsLock',
  'NumLock',
  'ScrollLock',
]);

function readMatchMedia(query: string): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(query).matches;
}

/** Pure read for tests and SSR-safe defaults. */
export function readInputCapabilities(): InputCapabilities {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return {
      coarsePointer: false,
      finePointer: true,
      canHover: true,
      hasHardwareKeyboard: true,
    };
  }
  const coarsePointer = readMatchMedia('(pointer: coarse)');
  const finePointer = readMatchMedia('(pointer: fine)');
  const canHover = readMatchMedia('(hover: hover)');
  return {
    coarsePointer,
    finePointer,
    canHover,
    hasHardwareKeyboard: finePointer && canHover,
  };
}

/**
 * Input capability flags for gating keyboard / hover affordances.
 * `hasHardwareKeyboard` starts as finePointer && canHover, then locks true on any
 * non-modifier keydown (external keyboard hot-plug on touch devices).
 */
export function useInputCapabilities(): InputCapabilities {
  const [mediaCaps, setMediaCaps] = useState(readInputCapabilities);
  const keyboardLockedRef = useRef(false);
  const [, bump] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const queries = ['(pointer: coarse)', '(pointer: fine)', '(hover: hover)'] as const;
    const mqs = queries.map((q) => window.matchMedia(q));
    const sync = () => setMediaCaps(readInputCapabilities());
    sync();
    for (const mq of mqs) {
      mq.addEventListener('change', sync);
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (MODIFIER_KEYS.has(event.key)) return;
      if (keyboardLockedRef.current) return;
      keyboardLockedRef.current = true;
      bump((n) => n + 1);
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });

    return () => {
      for (const mq of mqs) {
        mq.removeEventListener('change', sync);
      }
      window.removeEventListener('keydown', onKeyDown, { capture: true });
    };
  }, []);

  return {
    ...mediaCaps,
    hasHardwareKeyboard: keyboardLockedRef.current || mediaCaps.hasHardwareKeyboard,
  };
}
